import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Final logo: white ball with dimples, gold tee, red flag
const GreenTimeLogo = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 180 180">
    <defs>
      <clipPath id="logoClip"><rect width="180" height="180" rx="10"/></clipPath>
      <radialGradient id="logoBg" cx="40%" cy="35%" r="65%">
        <stop offset="0%" stopColor="#1a4a2a"/>
        <stop offset="100%" stopColor="#0d2b1a"/>
      </radialGradient>
      <radialGradient id="logoBall" cx="38%" cy="32%" r="58%">
        <stop offset="0%" stopColor="#ffffff"/>
        <stop offset="60%" stopColor="#f0f0e8"/>
        <stop offset="100%" stopColor="#d8d8d0"/>
      </radialGradient>
    </defs>
    <g clipPath="url(#logoClip)">
      <rect width="180" height="180" fill="url(#logoBg)"/>
      <circle cx="90" cy="90" r="70" fill="#0f3520"/>
      <circle cx="90" cy="90" r="68" fill="none" stroke="#7ec99a" strokeWidth="1.5" opacity="0.5"/>
      {/* Hour markers */}
      <line x1="90" y1="26" x2="90" y2="35" stroke="#7ec99a" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="90" y1="145" x2="90" y2="154" stroke="#7ec99a" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="26" y1="90" x2="35" y2="90" stroke="#7ec99a" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="145" y1="90" x2="154" y2="90" stroke="#7ec99a" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="135" y1="45" x2="130" y2="52" stroke="#7ec99a" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      <line x1="45" y1="45" x2="50" y2="52" stroke="#7ec99a" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      <line x1="45" y1="135" x2="50" y2="128" stroke="#7ec99a" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      <line x1="135" y1="135" x2="130" y2="128" stroke="#7ec99a" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      {/* Hour hand: ball on tee at 10 o'clock */}
      <g transform="rotate(-60, 90, 90) translate(90, 90) scale(0.44) translate(-50, -117)">
        {/* Gold tee body */}
        <path fill="#d4b86a" d="m47.988 68.215c-0.19141-5.0469-0.29688-10.102-0.60938-15.141-0.16797-2.6953-0.89453-5.3086-2.1367-7.7422-0.23047-0.45312-0.60938-0.82812-0.82422-1.2852-0.19922-0.42188-0.35938-0.89844-0.38281-1.3594-0.03125-0.60938-0.24609-0.93359-0.82031-1.1992-5.8047-2.7031-9.1523-7.25-9.9609-13.594-1.1289-8.8359 5.2539-17.41 14.656-18.574 7.9531-0.98437 15.578 3.6523 18.062 11.262 2.7734 8.5039-1.0078 17.316-9.168 20.945-0.65234 0.28906-0.89844 0.62891-0.98047 1.3359-0.066407 0.58203-0.51562 1.1133-0.74219 1.6875-0.67969 1.7109-1.4492 3.3945-1.9727 5.1523-0.82031 2.7539-0.87891 5.6289-0.89453 8.4727-0.042969 7.2852-0.039063 14.574 0.070312 21.863 0.046875 3.2227-0.51172 6.3281-1.2109 9.4336-0.066407 0.29688-0.17578 0.59766-0.33203 0.85547-0.40234 0.66797-1.2383 0.66016-1.6094-0.03125-0.20312-0.37891-0.31641-0.8125-0.41797-1.2305-0.83203-3.457-1.2109-6.9453-1.0625-10.52 0.14062-3.4336 0.23438-10.32 0.33594-10.32z"/>
        {/* White ball */}
        <circle cx="50" cy="22" r="18" fill="url(#logoBall)"/>
        {/* Dimples */}
        <path fill="#909088" d="m49.051 25.219c0.32031 0.070312 0.70312 0.054688 0.94922 0.22656 0.8125 0.57813 0.90625 1.4609 0.74609 2.3516-0.15625 0.86328-0.57422 1.6016-1.4648 1.9219-0.70312 0.25391-1.4648-0.082031-1.8867-0.81641-0.57812-1.0156-0.51953-2.0703-0.058593-3.1016 0.082031-0.18359 0.41406-0.25 0.63281-0.37109-0.019531 0.21875 0.007812 0.45703-0.066406 0.65625-0.28906 0.77734-0.39062 1.5664 0.003906 2.3047 0.15625 0.29297 0.54297 0.58984 0.85938 0.62891 0.27734 0.035157 0.70703-0.21484 0.87891-0.46875 0.61328-0.91406 0.62109-2.2148-0.39453-2.7656-0.10547-0.054687-0.17578-0.17578-0.25781-0.26562 0.019531-0.10156 0.042968-0.19922 0.0625-0.30078z"/>
        <path fill="#909088" d="m46.367 36.039c0-0.87109 0.73437-1.625 1.5781-1.6289 0.85156-0.003906 1.6484 0.92188 1.6289 1.8906-0.019531 0.99609-0.74609 1.8125-1.6094 1.8125-0.90234-0.003906-1.6016-0.91016-1.6016-2.0742z"/>
        <path fill="#909088" d="m53.258 31.719c-0.10156 0.80859-0.42187 1.4961-1.1719 1.9258-0.91016 0.52734-1.8125 0.11328-2.0195-0.92578-0.26562-1.3281 0.99219-2.6445 2.3203-2.3828 0.71094 0.14062 0.80859 0.75391 0.875 1.3789z"/>
        <path fill="#909088" d="m43.891 31.562c0.11328-1.0547 0.61719-1.9531 1.2617-2.0859 0.66406-0.13672 1.3906 0.48047 1.7852 1.5039 0.28516 0.73828 0.32031 1.5078-0.14844 2.125-0.30469 0.40234-0.85547 0.78125-1.332 0.83203-0.33203 0.035156-0.83984-0.4375-1.0664-0.80469-0.27734-0.45703-0.34375-1.043-0.50391-1.5703z"/>
        <path fill="#909088" d="m56.301 26.723c-0.3125 0.625-0.52734 1.332-0.96484 1.8516-0.59766 0.71094-1.5156 0.53906-1.9922-0.26562-0.53516-0.89844-0.47656-1.8125 0.027344-2.7031 0.25391-0.44531 1.3359-0.76953 1.8633-0.59375 0.55469 0.18359 0.92188 0.84766 1.0664 1.707z"/>
        <path fill="#909088" d="m43.855 26.219c-0.25 0.62891-0.40234 1.3242-0.76953 1.875-0.62109 0.94141-1.8008 0.76562-2.2422-0.26953-0.48828-1.1484 0.10156-2.6602 1.2344-3.1602 0.84375-0.37109 1.668 0.37891 1.7734 1.5508z"/>
        <path fill="#909088" d="m41.57 21.078c0.58594 0.74609 0.37891 1.3086 0.085938 1.8125-0.57812 0.99609-1.8516 1.4688-2.6133 1.0234-0.87109-0.50781-1.25-2.0195-0.66797-3.0195 0.24219-0.41797 0.65234-0.76953 1.0625-1.0312 0.14844-0.09375 0.51953 0.15234 0.78906 0.24609-0.14844 0.1875-0.25781 0.44922-0.44922 0.55469-0.69531 0.37891-1.1328 1.5938-0.71094 2.2578 0.13672 0.21875 0.55859 0.5 0.69922 0.43359 0.48047-0.22656 0.96484-0.53516 1.3125-0.93359 0.25781-0.29688 0.30078-0.78125 0.49219-1.3438z"/>
        <path fill="#909088" d="m60.676 24.051c0.11719 0.33594 0.34766 0.67578 0.33203 1.0039-0.035156 0.96094-0.4375 1.7734-1.2656 2.332-0.84375 0.57031-1.4844 0.42969-1.9805-0.42969-0.43359-0.75-0.30469-2.1562 0.28516-2.7344 0.12891-0.12891 0.37891-0.13281 0.57422-0.19531-0.027344 0.18359-0.003906 0.39844-0.089844 0.54297-0.36328 0.60938-0.44922 1.2344-0.21484 1.9062 0.20703 0.58594 0.53906 0.70313 1.043 0.33594 0.64062-0.46484 0.95312-1.1094 0.96484-1.9062 0.003906-0.26562 0.039062-0.52734 0.0625-0.78906 0.09375-0.023438 0.19141-0.046876 0.28516-0.066407z"/>
      </g>
      {/* Minute hand: red flag pin at 2 o'clock */}
      <g transform="rotate(60, 90, 90)">
        <line x1="90" y1="90" x2="90" y2="28" stroke="#e8e8e0" strokeWidth="2.5" strokeLinecap="round"/>
        <polygon points="90,28 116,38 90,48" fill="#cc2200"/>
        <polygon points="90,28 116,38 90,48" fill="none" stroke="#aa1800" strokeWidth="0.75"/>
      </g>
      <circle cx="90" cy="90" r="5.5" fill="#7ec99a"/>
      <circle cx="90" cy="90" r="3" fill="#0d2b1a"/>
    </g>
  </svg>
);

const Logo = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <GreenTimeLogo size={36} />
    <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 19,
                   color: '#e8f5ec', fontWeight: 500 }}>
      Green<span style={{ color: '#7ec99a' }}>time</span>
    </span>
  </div>
);

export { GreenTimeLogo };

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [shareCopied, setShareCopied] = useState(false);

  const handleLogout = () => { logout(); navigate('/auth'); };

  // v98: app-level share — uses the native share sheet on mobile (text,
  // email, etc.) when available, falling back to copying a share-ready
  // string to the clipboard on desktop browsers that don't support it.
  const handleShareApp = async () => {
    const shareText = 'Join me on Greentime, a tee time coordination app';
    const shareUrl = window.location.origin;
    if (navigator.share) {
      try { await navigator.share({ title: 'Greentime', text: shareText, url: shareUrl }); }
      catch { /* user cancelled the share sheet — not an error */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch { /* clipboard blocked — nothing more we can do here */ }
    }
  };

  const navStyle = ({ isActive }) => ({
    padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500,
    cursor: 'pointer', color: isActive ? '#0d2b1a' : 'rgba(255,255,255,0.55)',
    background: isActive ? '#7ec99a' : 'transparent',
    textDecoration: 'none', whiteSpace: 'nowrap', transition: 'all 0.15s',
  });

  return (
    <div>
      <header style={{ background: '#0d2b1a',
                       padding: '0.85rem 1.4rem',
                       // v85b.1: extend top padding by the iOS status-bar safe area so the
                       // clock/dynamic-island doesn't overlap the Greentime wordmark in PWA
                       // standalone mode. env(safe-area-inset-top) is 0 on Android and
                       // non-PWA browsers, so this is a no-op there.
                       paddingTop: 'calc(0.85rem + env(safe-area-inset-top, 0px))',
                       display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                       gap: '0.75rem', flexWrap: 'wrap' }}>
        <Logo />
        <nav style={{ display: 'flex', gap: 3, background: 'rgba(255,255,255,0.07)',
                      borderRadius: 8, padding: 3, flexWrap: 'wrap' }}>
          <NavLink to="/browse"  style={navStyle}>Tee Times</NavLink>
          <NavLink to="/my"      style={navStyle}>My Schedule</NavLink>
          <NavLink to="/groups"  style={navStyle}>My Groups</NavLink>
          <NavLink to="/friends" style={navStyle}>Friends</NavLink>
          <NavLink to="/profile" style={navStyle}>Profile</NavLink>
          <NavLink to="/help"    style={navStyle}>Help</NavLink>
          {user?.is_admin && <NavLink to="/admin" style={navStyle}>Admin</NavLink>}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={handleShareApp}
            title="Share Greentime"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: 'rgba(255,255,255,0.09)', border: 'none', cursor: 'pointer',
              fontSize: 14, color: 'rgba(255,255,255,0.85)',
            }}
          >
            {shareCopied ? '✓' : '📤'}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                        background: 'rgba(255,255,255,0.09)', borderRadius: 20,
                        padding: '4px 12px 4px 4px', cursor: 'pointer' }}
               onClick={() => navigate('/profile')}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#2e7d4f',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 500, color: '#fff' }}>
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
              {user?.first_name}
            </span>
          </div>
        </div>
      </header>
      <main style={{ padding: '1.25rem 1.4rem', maxWidth: 980, margin: '0 auto' }}>
        <Outlet />
      </main>
    </div>
  );
}
